/* eslint camelcase: 0 */

import { primeOrder as editions } from '~/config/editions/ClientSafeEditions';

export function mapAmpDataVars(node) {
  if (!node) return null;
  const attrs = Array.from(node.attributes).filter((attr) => attr.name.match(/^data-vars-/) && attr.value);
  if (attrs.length === 0) return null;

  const JSON_ARRAY_ALLOWLIST = new Set([
    'data_source_algorithm',
    'data_source_algorithm_version',
  ]);

  const entries = attrs.map((attr) => {
    const name = attr.name.match(/^data-vars-(.*)/)[1].replace(/-/g, '_');
    let { value } = attr;

    // Only attempt JSON parsing for allowlisted attributes that expect arrays
    if (JSON_ARRAY_ALLOWLIST.has(name) && typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      try {
        const parsed = JSON.parse(value);
        // Additional validation: ensure it's actually an array and contains only strings
        if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
          value = parsed;
        }
        // If validation fails, keep the original string value
      } catch (e) {
        // If parsing fails, keep the original string value
      }
    }

    return [name, value];
  });
  return Object.fromEntries(entries);
}

function inSidebar(node) {
  // Assume a 10-year-old browser is running on a desktop
  let atDesktopWidth = true;
  if (typeof window.matchMedia === 'function') {
    // FIXME: keep this in sync with the actual breakpoint. So frustrating!
    atDesktopWidth = window.matchMedia('(min-width: 1024px)').matches;
  }
  return atDesktopWidth && !!node.closest('.js-right-container');
}

function isBelowDesktop() {
  let belowDesktop = false;
  if (typeof window.matchMedia === 'function') {
    // FIXME: keep this in sync with the actual breakpoint. So frustrating!
    belowDesktop = !window.matchMedia('(min-width: 1024px)').matches;
  }
  return belowDesktop;
}

function generateSelector(mainSelector, selectorArray) {
  let finalSelector = '';
  selectorArray.forEach((selector, index, array) => {
    finalSelector += `${mainSelector} ${selector}`;
    if (index !== array.length - 1) {
      finalSelector += ', ';
    }
  });
  return finalSelector;
}
export function positionInUnit(node, pageType, unitType, unitName, selector = []) {
  // As this is on a responsive page, we must figure out if the component is
  // being rendered in the right column (front-page only due to its structure), or in the main column (it never moves
  // positions in the markup).
  // For front if item is in sidebar and we are on desktop treat it as right unit, if its below desktop then there is no sidebar treat all as main unit, else it is desktop and not on sidebar so select all but exclude the ones on sidebar
  // For entries we have buzz_head, buzz_body, buzz_bottom on the main column and we have right sidebar and left sidebar (sharebar)
  let position = 0;
  let unitSelector = '';
  const itemSelectors = selector.length > 0 ? selector : ['.js-card[data-vars-type]', '.js-card-flex-pro[data-vars-type]', 'a[data-vars-type]:not(.js-card[data-vars-type] a)', 'button[data-vars-type]', '.js-cet-item', '.js-cet-subbuzz', 'a[data-vars-type]:not(.js-card-flex-pro[data-vars-type] a)'];
  const subunit = node.closest('.js-cet-subunit');
  if (!subunit) return null;

  if (pageType === 'buzz') {
    if (unitType === 'sidebar') {
      unitSelector = unitName === 'left' ? '.js-cet-unit-left' : '.js-cet-unit-right';
    } else if (unitName === 'site_header' || unitType === 'nav_bar') {
      unitSelector = '.js-cet-unit-site-header';
    } else if (unitName === 'top_ad') {
      unitSelector = '.js-cet-unit-top-ad';
    } else {
      unitSelector = `.js-cet-unit-${unitType}`;
    }
  } else if (inSidebar(subunit) || unitType === 'sidebar') {
    unitSelector = '.js-cet-unit-right';
  } else if (isBelowDesktop()) {
    unitSelector = '.js-cet-unit-main';
  } else if (unitName === 'site_header' || unitType === 'nav_bar') {
    unitSelector = '.js-cet-unit-site-header';
  } else if (unitName === 'top_ad') {
    unitSelector = '.js-cet-unit-top-ad';
  } else {
    const allUnitSelector = generateSelector('.js-cet-unit-main', itemSelectors);
    const rightUnitSelector = generateSelector('.js-cet-unit-right', itemSelectors);
    const allUnits = Array.from(document.querySelectorAll(allUnitSelector));
    const rightUnit = new Set(Array.from(document.querySelectorAll(rightUnitSelector)));
    const mainUnit = allUnits.filter((x) => !rightUnit.has(x));
    position = mainUnit.indexOf(node);
    if (position >= 0) {
      return position;
    }
  }

  const allItems = Array.from(document.querySelectorAll(generateSelector(unitSelector, itemSelectors)));
  position = allItems.indexOf(node);
  if (position >= 0) {
    return position;
  }
  return null;
}

export function unitDataVars(node, payload = {}) {
  const unit_name = payload && payload.unit_name ? payload.unit_name : 'main';
  const unit_type = payload && payload.unit_type ? payload.unit_type : 'feed';
  if (inSidebar(node)) {
    return { unit_name: 'right', unit_type: 'sidebar' };
  }
  return { unit_name, unit_type };
}

export function targetDataVars(headline) {
  const isNative = headline && headline.type === 'native';
  const isLinkout = headline && headline.type === 'linkout';
  const isInternalLinkout = isLinkout && headline.url && (
    headline.url.startsWith('/') || editions.some((e) => headline.url.startsWith(e.link))
  );
  const isInternal = isNative || isInternalLinkout;
  const type = isInternal ? 'web_internal_link' : 'web_external_link';

  let contentType = null;
  if (isInternal) {
    contentType = headline.url.includes('/entry/') ? 'buzz' : 'feed';
  }

  let contentId;
  try {
    if (isInternal) {
      contentId = headline.url.includes('/entry/')
        ? headline.entry_id
        : new URL(headline.url).pathname;
    } else {
      contentId = headline.url;
    }
  } catch (_) {
    // eslint
  }

  return { contentType, contentId, type };
}

export function splashItemtargetDataVars(url = {}) {
  const { value } = url;
  const isNative = url?.type === 'native';
  const isLinkout = url?.type === 'linkout';
  const isInternalLinkout = isLinkout && value && (
    value.startsWith('/') || editions.some((e) => value.startsWith(e.link))
  );
  const isInternal = isNative || isInternalLinkout;
  const type = isInternal ? 'web_internal_link' : 'web_external_link';

  let contentType = 'url';
  if (isInternal) {
    contentType = value.includes('/entry/') ? 'buzz' : 'feed';
  }

  let contentId;
  try {
    if (isInternal) {
      contentId = value.includes('/entry/')
        ? url.entry_id
        : new URL(value).pathname;
    } else {
      contentId = value;
    }
  } catch (_) {
    // eslint
  }

  return { contentType, contentId, type };
}
